export default function promiseTimeout<T, E extends Error>(options: {
  promise: Promise<T>;
  timeout: number;
  error?: string | ((originalError?: Error) => E);
  promiseError?: string | ((originalError?: Error) => E);
  onTimeout?(): void;
}): Promise<T> {
  const { promise, timeout } = options;
  let timeoutID: ReturnType<typeof setTimeout>;
  let pending = true;
  const rejectingPromise = new Promise<T>((resolve, reject) => {
    timeoutID = setTimeout(() => {
      if (pending) {
        const rejection = (typeof options.error === 'string' || !options.error)
          ? new Error(options.error || `Timeout of ${timeout} exceeded`)
          : options.error();
        reject(rejection);
        options.onTimeout?.();
      }
    }, timeout);
  });

  return Promise.race([
    promise.then((result) => {
      pending = false;
      // Do not throw timeout error, when promise is fulfilled.
      // Muted exception anyway triggers unexpected breakpoint within debug session.
      if (timeoutID)
        clearInterval(timeoutID);
      return result;
    }, (error_) => {
      pending = false;
      // Do not throw timeout error, when promise is rejected.
      // Muted exception anyway triggers unexpected breakpoint within debug session.
      if (timeoutID)
        clearInterval(timeoutID);

      const rejection = (typeof options.promiseError === 'string' || !options.promiseError)
        ? error_
        : options.promiseError(error_);

      return Promise.reject(rejection);
    }),
    rejectingPromise,
  ]);
}
